import { useCallback, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { LuCheck, LuEye, LuLoader2, LuMoreHorizontal, LuUserX } from "react-icons/lu";
import { SITELINKS } from "../../../app-routes/links";
import useDismiss, { DismissReason } from "../../../hooks/useDismiss";
import { handleMenuKeys } from "../../../app-components/header/menuKeys";

type Props = {
  userId: number;
  eliteStatus?: string | null;
  busy: boolean;
  /** open the menu above the button (used for the last rows) */
  openUp?: boolean;
  onApprove: () => void;
  onRemove: () => void;
};

const item =
  "flex w-full items-center gap-2.5 px-3.5 py-2 text-left text-sm transition-colors focus:outline-none";

/** Click-to-open row menu (works on touch screens), showing only actions that apply */
const UserRowActions = ({ userId, eliteStatus, busy, openUp, onApprove, onRemove }: Props) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  const status = String(eliteStatus ?? "").toUpperCase();
  const canApprove = status !== "APPROVED";
  const canRemove = status !== "REMOVED";

  const dismiss = useCallback((reason: DismissReason) => {
    setOpen(false);
    if (reason === "escape") btnRef.current?.focus();
  }, []);
  useDismiss(wrapRef, open, dismiss);

  const run = (fn: () => void) => {
    setOpen(false);
    fn();
  };

  return (
    <div ref={wrapRef} className="relative inline-block">
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={busy}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={busy ? "Saving" : "Actions"}
        className="flex h-8 w-8 items-center justify-center rounded-lg text-honeConnectGray transition-colors hover:bg-hcBg hover:text-honeConnectBlack disabled:cursor-wait focus:outline-none focus-visible:ring-2 focus-visible:ring-honeConnectPurple/40"
      >
        {busy ? (
          <LuLoader2 className="h-4 w-4 animate-spin" aria-hidden />
        ) : (
          <LuMoreHorizontal className="h-5 w-5" aria-hidden />
        )}
      </button>

      {open && (
        <div
          role="menu"
          onKeyDown={handleMenuKeys}
          className={`absolute right-0 z-30 w-48 overflow-hidden rounded-xl border border-hcBorder bg-white py-1.5 text-left shadow-pop ${
            openUp ? "bottom-full mb-1" : "top-full mt-1"
          }`}
        >
          {canApprove && (
            <button type="button" role="menuitem" onClick={() => run(onApprove)} className={`${item} text-honeConnectBlack hover:bg-hcBg focus:bg-hcBg`}>
              <LuCheck className="h-4 w-4 text-emerald-600" aria-hidden />
              Approve
            </button>
          )}
          <Link to={SITELINKS.USER_PROFILE + userId} role="menuitem" className={`${item} text-honeConnectBlack hover:bg-hcBg focus:bg-hcBg`}>
            <LuEye className="h-4 w-4 text-hcMuted" aria-hidden />
            View profile
          </Link>
          {canRemove && (
            <>
              <div className="my-1 border-t border-hcBorder" />
              <button type="button" role="menuitem" onClick={() => run(onRemove)} className={`${item} text-red-600 hover:bg-red-50 focus:bg-red-50`}>
                <LuUserX className="h-4 w-4" aria-hidden />
                Remove
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default UserRowActions;
